import { useEffect } from 'react';
import Animated, {
  Easing,
  FadeIn,
  FadeOut,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { colors, radius, type } from '@/theme';

/** Long enough to read twice, short enough not to sit on the board. */
const TIMEOUT_MS = 5200;

type Props = {
  /** Fired on tap and on timeout — the caller decides what "seen" means. */
  onDismiss: () => void;
};

/**
 * Teaches the long press, once per install. Absolutely positioned so it hangs
 * off the row it points at and floats over whatever is beneath: nothing on the
 * board moves when it arrives or leaves, which is what separates a hint from a
 * piece of content.
 *
 * The parent has to give the pointed-at row the winning z-index while this is
 * up, otherwise later rows paint over it.
 */
export function LongPressHint({ onDismiss }: Props) {
  const bob = useSharedValue(0);

  useEffect(() => {
    bob.value = withRepeat(withTiming(1, { duration: 1300, easing: Easing.inOut(Easing.quad) }), -1, true);
  }, [bob]);

  useEffect(() => {
    const timer = setTimeout(onDismiss, TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const float = useAnimatedStyle(() => ({ transform: [{ translateY: bob.value * -3 }] }));

  return (
    <Animated.View
      entering={FadeIn.duration(260)}
      exiting={FadeOut.duration(180)}
      style={styles.anchor}
      pointerEvents="box-none">
      <Animated.View style={float}>
        <View style={styles.caret} />
        <Pressable onPress={onDismiss} style={styles.bubble}>
          <Text style={styles.text}>
            <Text style={styles.strong}>Hold</Text> a name to see who they are
          </Text>
        </Pressable>
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  anchor: {
    position: 'absolute',
    top: '100%',
    left: 24,
    marginTop: 5,
  },
  caret: {
    marginLeft: 14,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderBottomWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: colors.gold,
  },
  bubble: {
    backgroundColor: colors.gold,
    borderRadius: radius.sm,
    paddingHorizontal: 12,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOpacity: 0.45,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 5 },
    elevation: 8,
  },
  text: {
    ...type.body,
    fontSize: 12.5,
    color: colors.onGold,
  },
  strong: {
    ...type.title,
    fontSize: 12.5,
    color: colors.onGold,
  },
});
